import { useApolloClient, useMutation } from "@apollo/client/react";
import {
  DSButton,
  DSCircularButton,
} from "@components/design-system/molecules";
import {
  faArrowLeft,
  faHatWizard,
} from "@fortawesome/pro-solid-svg-icons";
import { useWatchUserWsEvent } from "@hooks";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { DSModal } from "../../design-system/organisms/DSModal";
import { TRANSCRIPT_SAMPLE_MAPPINGS } from "../constants";
import ShareTemplateModal from "../ShareTemplateModal";
import TemplateWizard from "../TemplateWizard";
import { UpdateTeamDefaultButton } from "../UpdateTeamDefaultButton";
import { resetTemplateFields } from "../utils";
import FormBuilderSection from "./components/FormBuilderSection";
import MetadataSection from "./components/MetadataSection";
import PreviewSection from "./components/PreviewSection";
import TemplateActionsToolbar from "./components/TemplateActionsToolbar";
import ConfirmAbandonEditsModal from "./modals/ConfirmAbandonEditsModal";
import ConfirmTemplateConstructionModal from "./modals/ConfirmTemplateConstructionModal";
import ConfirmTemplateDeleteModal from "./modals/ConfirmTemplateDeleteModal";
import ConfirmTemplateTypeModal from "./modals/ConfirmTemplateTypeModal";
import NotePickerModal from "./modals/NotePickerModal";
import ResetTemplateModal from "./modals/ResetTemplateModal";
import { isTemplateSaveable } from "./utils";
import { CREATE_TEMPLATE_PREVIEW } from "../../../graphql/mutations/AutoTemplates";
import { GET_AUTO_TEMPLATE_BY_UUID } from "../../../graphql/queries/AutoTemplates";
import { usePdfTemplateExtraction } from "../../../hooks/usePdfTemplateExtraction";
import { alert } from "../../common/Alert";

const TemplateBuilder = ({
  control,
  title,
  isNew = false,
  handleDelete,
  shouldShowDeleteModal,
  setShouldShowDeleteModal,
  refetchSharingOnly,
  templateConstructionChoice,
  setTemplateConstructionChoice,
  previewContent,
  setPreviewContent,
  doesCurrentUserOwnThisTemplate,
  onBackClick,
  formRef,
  showSaveSuccess,
  onDismissSaveSuccess,
}) => {
  const { getValues, setValue, formState } = useFormContext();
  const { dirtyFields, isSubmitting } = formState;
  const formData = useWatch({ control });
  const { releaseWebAdaptiveTemplateWizard } = useFlags();
  const [showWizard, setShowWizard] = useState(
    isNew && !!releaseWebAdaptiveTemplateWizard,
  );

  // Navigation and URL Parameters Hooks
  const navigate = useNavigate();
  const { uuid: templateUuid } = useParams();

  const apolloClient = useApolloClient();
  const previewTimeoutRef = useRef(null);
  const pendingPreviewRef = useRef(null);

  // State Declarations
  const [loadingExtraction, setLoadingExtraction] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [
    showConfirmTemplateTypeModal,
    setShowConfirmTemplateTypeModal,
  ] = useState(false);
  const [
    showConfirmTemplateConstructionModal,
    setShowConfirmTemplateConstructionModal,
  ] = useState(false);
  const [
    showResetTemplateConstructionModal,
    setShowResetTemplateConstructionModal,
  ] = useState(false);
  const [
    showConfirmAbandonEditsModal,
    setShowConfirmAbandonEditsModal,
  ] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showConfirmWizardSwitch, setShowConfirmWizardSwitch] =
    useState(false);

  const [highlightFileUploader, setHighlightFileUploader] =
    useState(false);
  // Note picker state
  const [showNotePickerModal, setShowNotePickerModal] =
    useState(false);
  const [previewSource, setPreviewSource] = useState("sample"); // "sample" or "note"
  const [selectedNote, setSelectedNote] = useState(null);

  // Mutations
  const [createTemplatePreview] = useMutation(
    CREATE_TEMPLATE_PREVIEW,
  );

  const { extractTemplateFromPdf } = usePdfTemplateExtraction();

  const previewSectionRef = useRef(null);

  const clearPreviewTimeout = () => {
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
  };

  const fetchPreviewContent = async () => {
    if (!templateUuid) {
      throw new Error("Missing template UUID for preview");
    }
    const maxAttempts = 3;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const { data } = await apolloClient.query({
        query: GET_AUTO_TEMPLATE_BY_UUID,
        variables: { uuid: templateUuid },
        fetchPolicy: "no-cache",
      });
      const preview =
        data?.autoTemplateById?.templatePreviews?.[0]?.previewContent;
      if (preview) {
        return preview;
      }
      await new Promise((resolve) =>
        setTimeout(resolve, 500 * (attempt + 1)),
      );
    }
    throw new Error("Preview content not available yet");
  };

  const handlePreviewMessage = async (message) => {
    if (!pendingPreviewRef.current) {
      return;
    }
    if (message?.data?.template_uuid != templateUuid) {
      return;
    }
    if (message?.event === "template_preview_complete") {
      try {
        const preview = await fetchPreviewContent();
        if (preview) {
          setPreviewContent(preview);
        }
        alert(
          "success",
          `Your preview has been successfully generated. Check it out! If you're happy with it, click "Save Template".`,
        );
        mixpanel.track("Template preview complete", {
          templateUuid: message?.data?.template_uuid,
          templateName: message?.data?.template_name,
        });
      } catch {
        alert(
          "error",
          "There was an error updating the template preview. Please refresh your page.",
        );
      } finally {
        clearPreviewTimeout();
        pendingPreviewRef.current = null;
        setLoadingPreview(false);
      }
    } else if (message?.event === "template_preview_failed") {
      mixpanel.track("Template preview failed", {
        templateUuid: message?.data?.template_uuid,
        templateName: message?.data?.template_name,
      });
      clearPreviewTimeout();
      pendingPreviewRef.current = null;
      setLoadingPreview(false);
    }
  };

  useWatchUserWsEvent({
    onMessage: handlePreviewMessage,
  });

  useEffect(() => {
    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
    };
  }, []);

  const handlePreview = async () => {
    setLoadingPreview(true);
    pendingPreviewRef.current = templateUuid;
    clearPreviewTimeout();

    const {
      templateFields,
      templateType,
      userPreferences,
      sampleTranscript,
    } = getValues();

    mixpanel.track("Template preview requested", {
      templateUuid,
      templateType,
      previewSource: "sample",
      hasUserPreferences: !!userPreferences,
    });

    const validTemplateFields =
      templateFields?.filter((field) => {
        const { fieldName, fieldValue, fieldIdx } = field || {};
        return (
          fieldName &&
          typeof fieldValue === "string" &&
          fieldIdx !== undefined
        );
      }) || [];

    // Use the correct transcript based on preview source
    const transcriptToUse =
      previewSource === "note" && selectedNote
        ? selectedNote.transcription
        : sampleTranscript;

    const createTemplatePreviewInput = {
      templateFields: validTemplateFields,
      templateType,
      userPreferences,
      templateUuid,
      sampleTranscript: transcriptToUse,
    };

    try {
      await createTemplatePreview({
        variables: { createTemplatePreviewInput },
      });

      previewTimeoutRef.current = setTimeout(() => {
        alert(
          "warning",
          "It has been two minutes without a successful preview generation. Please try again.",
        );
        pendingPreviewRef.current = null;
        setLoadingPreview(false);
      }, 120000); // 2-minute timeout

      // Scroll to preview section
      previewSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } catch {
      clearPreviewTimeout();
      pendingPreviewRef.current = null;
      alert("error", "There was an error generating the preview.");
      setLoadingPreview(false);
    }
  };

  // File Upload Handlers
  const handleFileUpload = async (file) => {
    setLoadingExtraction(true);
    try {
      const formData = getValues();
      await extractTemplateFromPdf({
        file,
        templateType: formData?.templateType,
        onExtract: (updatedFields) => {
          setValue("templateFields", updatedFields, {
            shouldDirty: true,
          });
        },
      });
      mixpanel.track("PDF extraction completed", {
        templateUuid,
        templateType: formData?.templateType,
        success: true,
      });
      return true;
    } catch (err) {
      mixpanel.track("PDF extraction completed", {
        templateUuid,
        templateType: getValues("templateType"),
        success: false,
        error: err?.message,
      });
      alert(
        "error",
        "There was an error extracting the text from your PDF. Try again or use a different PDF.",
      );
      return false;
    } finally {
      setLoadingExtraction(false);
    }
  };

  const handleFileChange = (file) => {
    if (file?.type === "application/pdf") {
      handleFileUpload(file);
    } else {
      alert("warning", "Please upload a valid PDF file.");
    }
  };

  // Template Type Change Handler
  const pendingTemplateTypeRef = useRef(null);
  const handleTemplateTypeChange = (e) => {
    const previousType = getValues("templateType");
    const newType = e.target.value;
    const needsConfirmation = openConfirmationOpenIfFieldsFilled(
      setShowConfirmTemplateTypeModal,
    );
    if (needsConfirmation) {
      pendingTemplateTypeRef.current = newType;
    } else {
      refreshTemplateFields(newType);
    }
    mixpanel.track("Template type changed", {
      templateUuid,
      previousType,
      newType,
    });
  };

  // Utility Handler: Opens confirmation modal if any fields have values
  const openConfirmationOpenIfFieldsFilled = (setShowModal) => {
    const formData = getValues();
    if (formData.templateFields.some((field) => field.fieldValue)) {
      setShowModal(true);
      return true;
    }
  };

  // Utility Handler: Refreshes the template fields based on the new template type
  const refreshTemplateFields = (newTemplateType) => {
    resetTemplateFields(setValue, newTemplateType);
  };

  // Modal Confirmations / Handlers for Changing Template Type and Construction
  const confirmChangeTemplateType = () => {
    setShowConfirmTemplateTypeModal(false);
    if (pendingTemplateTypeRef.current) {
      refreshTemplateFields(pendingTemplateTypeRef.current);
      pendingTemplateTypeRef.current = null;
    }
  };

  const confirmChangeTemplateConstruction = () => {
    const newConstructionChoice =
      templateConstructionChoice === "upload" ? "scratch" : "upload";
    setTemplateConstructionChoice(newConstructionChoice);
    setShowConfirmTemplateConstructionModal(false);
    const templateType = getValues("templateType");
    refreshTemplateFields(templateType);
  };

  const cancelChangeTemplateType = () => {
    setShowConfirmTemplateTypeModal(false);
    pendingTemplateTypeRef.current = null;
  };

  const handleClearInspirationChips = () => {
    setValue(
      "templateFields",
      getValues("templateFields").map((field) => ({
        ...field,
        fieldValue: "",
      })),
      {
        shouldDirty: true,
      },
    );
    setValue("userPreferences", "");
    setValue("templateVariant", "DEFAULT");
    setValue(
      "sampleTranscript",
      TRANSCRIPT_SAMPLE_MAPPINGS.DEFAULT.sampleTranscript,
    );
  };

  const handleClearSampleTranscript = () => {
    setValue(
      "sampleTranscript",
      TRANSCRIPT_SAMPLE_MAPPINGS.DEFAULT.sampleTranscript,
      {
        shouldDirty: true,
      },
    );
  };

  const handleConfirmResetTemplateConstruction = () => {
    const templateType = getValues("templateType");
    refreshTemplateFields(templateType);
    setTemplateConstructionChoice("upload");
    setShowResetTemplateConstructionModal(false);
    handleClearInspirationChips();
    handleClearSampleTranscript();
  };

  // Handler for highlighting the file uploader
  const handleHighlightClick = () => {
    setHighlightFileUploader(true);
    setTimeout(() => setHighlightFileUploader(false), 500);
  };

  const handleConfirmAbandonEdits = () => {
    if (isNew) {
      handleDelete(true);
    } else {
      navigate("/templates");
    }
  };

  const handleBackClick = () => {
    if (Object.keys(dirtyFields).length > 0) {
      setShowConfirmAbandonEditsModal(true);
    } else {
      onBackClick();
    }
  };

  const handleOpenShareModal = () => {
    setShowShareModal(true);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  // Note picker handlers
  const handleOpenNotePicker = () => {
    setShowNotePickerModal(true);
  };

  const handleSelectNote = (note) => {
    setSelectedNote(note);
    // Set the note's transcription as the sample transcript for preview
    setValue("sampleTranscript", note.transcription, {
      shouldDirty: true,
    });
  };

  const handleClearPreview = () => {
    setPreviewContent("");
  };

  return (
    <div className="flex flex-col p-md">
      {/* Modals */}
      <ConfirmTemplateTypeModal
        shouldShow={showConfirmTemplateTypeModal}
        hideModal={cancelChangeTemplateType}
        confirmAction={confirmChangeTemplateType}
      />
      <ConfirmTemplateConstructionModal
        shouldShow={showConfirmTemplateConstructionModal}
        hideModal={() =>
          setShowConfirmTemplateConstructionModal(false)
        }
        confirmAction={confirmChangeTemplateConstruction}
      />
      <ResetTemplateModal
        shouldShow={showResetTemplateConstructionModal}
        hideModal={() => setShowResetTemplateConstructionModal(false)}
        confirmAction={handleConfirmResetTemplateConstruction}
      />
      <ConfirmAbandonEditsModal
        shouldShow={showConfirmAbandonEditsModal}
        hideModal={() => setShowConfirmAbandonEditsModal(false)}
        confirmAction={handleConfirmAbandonEdits}
      />
      <ConfirmTemplateDeleteModal
        shouldShow={shouldShowDeleteModal}
        hideModal={() => setShouldShowDeleteModal(false)}
        confirmAction={() => handleDelete(true)}
      />
      <ShareTemplateModal
        shouldShow={showShareModal}
        hideModal={handleCloseShareModal}
        template={formData}
        refetch={refetchSharingOnly}
        source="template builder"
      />
      <NotePickerModal
        shouldShow={showNotePickerModal}
        hideModal={() => setShowNotePickerModal(false)}
        onSelectNote={handleSelectNote}
      />
      <DSModal
        isOpen={showConfirmWizardSwitch}
        onClose={() => setShowConfirmWizardSwitch(false)}
        title="Switch to Wizard Builder?"
        actions={[
          {
            label: "Cancel",
            variant: "cancel",
            onClick: () => setShowConfirmWizardSwitch(false),
          },
          {
            label: "Switch to Wizard",
            variant: "primary",
            onClick: () => {
              setShowConfirmWizardSwitch(false);
              mixpanel.track("Classic Switch to Wizard", {
                templateUuid,
              });
              setShowWizard(true);
            },
            dataTestId: "confirm-wizard-switch-button",
          },
        ]}
        dataTestId="confirm-wizard-switch-modal"
      >
        <p>
          Switching to the Wizard Builder may reset your current
          template content. <b>Any unsaved changes will be lost.</b>
        </p>
        <p>Are you sure you want to proceed?</p>
      </DSModal>

      <div className="flex items-center gap-md bg-primary/90 sticky top-16 z-20 backdrop-filter backdrop-blur-xs pt-md pb-sm -mx-md px-md">
        {onBackClick && (
          <DSCircularButton
            icon={faArrowLeft}
            onClick={handleBackClick}
            aria-label="Back to templates"
            dataTestId="back-to-templates-button"
            size="sm"
          />
        )}
        <h2 className="text-3xl font-semibold text-primary">
          {title}
        </h2>
        {isNew && releaseWebAdaptiveTemplateWizard && (
          <DSButton
            type="button"
            variant={showWizard ? "secondary" : "primary"}
            icon={showWizard ? undefined : faHatWizard}
            size="sm"
            onClick={() => {
              const next = !showWizard;
              if (next && Object.keys(dirtyFields).length > 0) {
                setShowConfirmWizardSwitch(true);
                return;
              }
              mixpanel.track(
                next
                  ? "Classic Switch to Wizard"
                  : "Wizard Switch to Classic",
                { templateUuid },
              );
              setShowWizard(next);
            }}
            dataTestId="wizard-view-toggle"
          >
            {showWizard
              ? "Switch to Classic Builder"
              : "Wizard Builder"}
          </DSButton>
        )}
        {!showWizard && (
          <div className="ml-auto flex items-center gap-sm flex-wrap">
            <TemplateActionsToolbar
              control={control}
              setShouldShowDeleteModal={setShouldShowDeleteModal}
              loadingExtraction={loadingExtraction}
              handlePreview={handlePreview}
              loadingPreview={loadingPreview}
              previewContent={previewContent}
              onClearPreview={handleClearPreview}
              doesCurrentUserOwnThisTemplate={
                doesCurrentUserOwnThisTemplate
              }
              templateUuid={templateUuid}
              templateName={formData?.name}
              formRef={formRef}
              onShare={handleOpenShareModal}
              isShareDisabled={
                !isTemplateSaveable(formData) ||
                Object.keys(dirtyFields).length > 0
              }
              shareDisabledTooltip={
                !isTemplateSaveable(formData)
                  ? "Template must have a name and content"
                  : Object.keys(dirtyFields).length > 0
                    ? "Please save your changes before sharing this template"
                    : ""
              }
            />
            <UpdateTeamDefaultButton
              template={formData}
              onSuccess={refetchSharingOnly}
              disabled={
                !isTemplateSaveable(formData) ||
                Object.keys(dirtyFields).length > 0
              }
              source="template builder"
              disabledReason={
                !isTemplateSaveable(formData)
                  ? "Template must have a name and content"
                  : Object.keys(dirtyFields).length > 0
                    ? "Please save your changes before setting as team default"
                    : ""
              }
            />
          </div>
        )}
      </div>

      {showWizard ? (
        <TemplateWizard
          control={control}
          loadingExtraction={loadingExtraction}
          loadingPreview={loadingPreview}
          previewContent={previewContent}
          handleFileUpload={handleFileUpload}
          handlePreview={handlePreview}
          handleClearSampleTranscript={handleClearSampleTranscript}
          previewSource={previewSource}
          setPreviewSource={setPreviewSource}
          selectedNote={selectedNote}
          onSelectNote={handleSelectNote}
          onSave={() => formRef?.current?.requestSubmit()}
          saving={isSubmitting}
          clearPreview={() => setPreviewContent("")}
          showSuccessModal={showSaveSuccess}
          onCloseSuccessModal={onDismissSaveSuccess}
          onShareTemplate={() => {
            onDismissSaveSuccess();
            setShowShareModal(true);
          }}
        />
      ) : (
        <>
          {/* Form Builder */}
          <div className="flex flex-col mt-md space-y-8">
            <MetadataSection
              control={control}
              handleTemplateTypeChange={handleTemplateTypeChange}
            />
            <FormBuilderSection
              control={control}
              handleTemplateTypeChange={handleTemplateTypeChange}
              setShowResetTemplateConstructionModal={
                setShowResetTemplateConstructionModal
              }
              setShowConfirmTemplateConstructionModal={
                setShowConfirmTemplateConstructionModal
              }
              openConfirmationOpenIfFieldsFilled={
                openConfirmationOpenIfFieldsFilled
              }
              templateConstructionChoice={templateConstructionChoice}
              setTemplateConstructionChoice={
                setTemplateConstructionChoice
              }
              handleHighlightClick={handleHighlightClick}
              handleFileChange={handleFileChange}
              highlightFileUploader={highlightFileUploader}
              loadingExtraction={loadingExtraction}
            />
          </div>

          {/* Preview Section */}
          <div
            ref={previewSectionRef}
            className="border-t border-secondary pt-6 mt-8"
          >
            <h3 className="text-xl font-semibold text-primary mb-md">
              Preview
            </h3>
            <PreviewSection
              control={control}
              loadingPreview={loadingPreview}
              handleClearSampleTranscript={
                handleClearSampleTranscript
              }
              previewSource={previewSource}
              setPreviewSource={setPreviewSource}
              onOpenNotePicker={handleOpenNotePicker}
              selectedNote={selectedNote}
              previewContent={previewContent}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default TemplateBuilder;
